import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../contexts/ThemeProvider';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Card } from '../components/ui/Card';
import { RequireAuth } from '../auth/RequireAuth';
import { validateUsername, normalizeUsername } from '../lib/username';
import { saveUserProfile, getUserProfile } from '../lib/firestore';

function SettingsPageContent() {
  const { user, signOut, isDemoUser } = useAuth();
  const { theme: currentTheme, setTheme, availableThemes } = useTheme();
  const navigate = useNavigate();

  // Form state
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load existing profile data
  useEffect(() => {
    if (!user) return;

    if (isDemoUser) {
      setUsername('demo_cat');
      setDisplayName('Demo Cat');
      setBio('This is a demo account to explore the platform!');
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      try {
        const profile = await getUserProfile(user.uid);
        if (profile) {
          setUsername(profile.username || '');
          setDisplayName(profile.displayName || '');
          setBio(profile.bio || '');
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user, isDemoUser]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Failed to sign out:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const normalized = normalizeUsername(username);
    const validation = validateUsername(normalized);

    if (!validation.valid) {
      setError(validation.error || 'Invalid username');
      return;
    }

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (isDemoUser) {
      setSuccess('Settings saved! (Demo mode - changes are not persisted)');
      return;
    }

    setSaving(true);

    try {
      await saveUserProfile(user!.uid, {
        username: normalized,
        displayName: displayName.trim(),
        bio: bio.trim(),
      });
      setSuccess('Settings saved successfully!');
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl animate-bounce mb-4">⏳</div>
          <p className="text-gray-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gradient-to-b from-pink-50 to-white">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gradient">Settings</h1>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/editor')}>
              Back to Editor
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Sign Out
            </Button>
          </div>
        </div>

        {/* Demo Mode Banner */}
        {isDemoUser && (
          <div className="bg-yellow-100 border-2 border-yellow-400 text-yellow-800 px-4 py-3 rounded-xl mb-6">
            <strong>Demo Mode:</strong> Changes are not saved to the server.
          </div>
        )}

        {error && (
          <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border-2 border-green-400 text-green-700 px-4 py-3 rounded-xl mb-6">
            {success}
          </div>
        )}

        <Card className="mb-6">
          <h2 className="text-xl font-bold mb-4">Profile Information</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
                Username*
              </label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your_username"
                required
                disabled={saving}
              />
              <p className="text-xs text-gray-500 mt-1">
                3-20 characters, lowercase letters, numbers, and underscores only
              </p>
            </div>

            <div>
              <label htmlFor="displayName" className="block text-sm font-semibold text-gray-700 mb-2">
                Display Name*
              </label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your Name"
                required
                disabled={saving}
              />
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-semibold text-gray-700 mb-2">
                Bio
              </label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell people about yourself..."
                rows={4}
                disabled={saving}
              />
            </div>

            <Button type="submit" variant="primary" className="w-full" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </Card>

        <Card>
          <h2 className="text-xl font-bold mb-4">Theme</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {availableThemes.map((themeOption) => (
              <div
                key={themeOption.id}
                onClick={() => setTheme(themeOption.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${currentTheme.id === themeOption.id
                  ? 'border-primary shadow-pop'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
                style={{ backgroundColor: themeOption.backgroundColor }}
              >
                <div
                  className="w-full h-16 rounded-lg mb-2 shadow-sm flex items-center justify-center font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${themeOption.primaryColor}, ${themeOption.secondaryColor})` }}
                >
                  Aa
                </div>
                <p className="text-sm font-semibold text-center text-zinc-800">{themeOption.name}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function SettingsPage() {
  return (
    <RequireAuth>
      <SettingsPageContent />
    </RequireAuth>
  );
}
